import { load } from "@apple/mapkit-loader";
import type { Map as MapKitMap } from "apple-mapkit/mapkit";
import { getVenueLatLng, type WeddingDoc } from "./wedding/loadWedding";

let mapKitPromise: ReturnType<typeof load> | null = null;
let venueMap: MapKitMap | null = null;

function mapKitToken(): string {
  return (import.meta.env.VITE_MAPKIT_JS_TOKEN ?? "").trim();
}

/** iPhone / iPad: MapKit JS + karo önbelleği mobil Safari’de çok bellek kullanır; iframe daha hafif. */
function isIOSLikeDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod|iPad/i.test(ua)) return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
}

function openStreetMapEmbedUrl(lat: number, lng: number): string {
  const d = 0.012;
  const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lng}`)}`;
}

function appendOsmIframe(host: HTMLElement, lat: number, lng: number): void {
  const iframe = document.createElement("iframe");
  iframe.className = "wedy-map-frame";
  iframe.title = "Venue map";
  iframe.loading = "lazy";
  iframe.referrerPolicy = "no-referrer-when-downgrade";
  iframe.allowFullscreen = true;
  iframe.src = openStreetMapEmbedUrl(lat, lng);
  host.appendChild(iframe);
}

function ensureMapKit() {
  const token = mapKitToken();
  if (!token) return null;
  if (!mapKitPromise) {
    mapKitPromise = load({
      token,
      language: "en-US",
      libraries: ["map", "annotations"],
    });
  }
  return mapKitPromise;
}

export function destroyVenueMap(): void {
  if (venueMap) {
    try {
      venueMap.destroy();
    } catch {
      /* ignore */
    }
    venueMap = null;
  }
}

export async function mountVenueMapKit(root: HTMLElement, wedding: WeddingDoc): Promise<void> {
  destroyVenueMap();
  const coords = getVenueLatLng(wedding);
  if (!coords) return;

  const host = root.querySelector("[data-wedy-mapkit]");
  if (!(host instanceof HTMLElement)) return;

  host.replaceChildren();

  const title = (wedding.venueName ?? "").trim() || "Venue";
  const subtitle = (wedding.venueAddress ?? "").trim();

  if (isIOSLikeDevice() || !mapKitToken()) {
    appendOsmIframe(host, coords.lat, coords.lng);
    return;
  }

  const mkAwait = ensureMapKit();
  if (!mkAwait) {
    appendOsmIframe(host, coords.lat, coords.lng);
    return;
  }

  try {
    const mk = await mkAwait;
    const center = new mk.Coordinate(coords.lat, coords.lng);
    const span = new mk.CoordinateSpan(0.014, 0.014);
    const region = new mk.CoordinateRegion(center, span);

    const map = new mk.Map(host, {
      colorScheme: mk.Map.ColorSchemes.Dark,
      isRotationEnabled: false,
      showsMapTypeControl: false,
    });
    venueMap = map;
    map.region = region;

    const marker = new mk.MarkerAnnotation(center, {
      title,
      subtitle: subtitle || undefined,
    });
    map.addAnnotation(marker);
  } catch (e) {
    console.warn("[MapKit]", e);
    appendOsmIframe(host, coords.lat, coords.lng);
  }
}

/** Harita ekranda görünene kadar yüklenmez; ilk boyamada MapKit/OSM yükü bellek baskısını azaltır. */
export function mountVenueMapKitWhenVisible(root: HTMLElement, wedding: WeddingDoc): void {
  const host = root.querySelector("[data-wedy-mapkit]");
  if (!(host instanceof HTMLElement)) return;

  if (typeof IntersectionObserver === "undefined") {
    void mountVenueMapKit(root, wedding);
    return;
  }

  const io = new IntersectionObserver(
    (entries, obs) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      obs.disconnect();
      void mountVenueMapKit(root, wedding);
    },
    { root: null, rootMargin: "180px 0px", threshold: 0 },
  );
  io.observe(host);
}
